/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import type { ApiAsset, ApiTitle } from "@lumi/data";
import { Button } from "@lumi/ui-headless";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { useParams } from "react-router-dom";
import { useAuthenticatedClient } from "../../auth/AuthContext";
import { apiCall } from "../../config/api";
import { RAIL_DATA_ATTR, RAIL_SELECTOR } from "../../shared/constants/navigation";
import { DEVICE, VIEWPORT } from "../../shared/constants/ui";
import { useKeyboardNavigation } from "../../shared/contexts/KeyboardNavigationContext";
import { useTitleLike, useTransitionNavigation, useWatchlist } from "../../shared/hooks";
import { pageTransitions } from "../../shared/transitions/pageTransitions";
import { preloadPlayerData } from "../../shared/utils/playerPreload";
import { TitleContent, type TabType } from "./components";

/**
 * Available tabs for the Title page
 */
const TITLE_TABS: readonly TabType[] = ["details", "suggested", "extras"] as const;

/**
 * Helper to check if an element is a rail container
 */
function isRailElement(element: HTMLElement): boolean {
  return element.hasAttribute(RAIL_DATA_ATTR) && element.hasAttribute("tabindex");
}

/**
 * Check if keyboard event should be ignored (user is typing in input field)
 */
function shouldIgnoreKeyEvent(target: HTMLElement): boolean {
  return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;
}

/**
 * Handle Space key: Play title (unless on interactive element or rail)
 */
function handleSpaceKey(e: KeyboardEvent, target: HTMLElement, onPlay: () => void): boolean {
  if (e.key !== " ") return false;

  const isOnInteractive = target.tagName === "BUTTON" || target.tagName === "A";
  const railFocused = isRailElement(target);

  if (!isOnInteractive && !railFocused) {
    e.preventDefault();
    onPlay();
    return true;
  }

  return false;
}

/**
 * Handle W key: Toggle watchlist
 */
function handleWatchlistKey(
  e: KeyboardEvent,
  onToggleWatchlist: () => Promise<void>,
  watchlistMutating: boolean,
): boolean {
  if (e.key === "w" && !e.metaKey && !e.ctrlKey && !e.shiftKey && !watchlistMutating) {
    e.preventDefault();
    void onToggleWatchlist();
    return true;
  }
  return false;
}

/**
 * Handle L key: Toggle like
 */
function handleLikeKey(e: KeyboardEvent, onToggleLike: () => Promise<void>, likeMutating: boolean): boolean {
  if (e.key === "l" && !e.metaKey && !e.ctrlKey && !e.shiftKey && !likeMutating) {
    e.preventDefault();
    void onToggleLike();
    return true;
  }
  return false;
}

/**
 * Handle Up/Down keys: Toggle tabs expanded (when not navigating between rails)
 */
function handleArrowUpDown(
  e: KeyboardEvent,
  target: HTMLElement,
  onToggleTabsExpanded: () => void,
  isDeviceLandscape: boolean,
): boolean {
  if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return false;

  // In phone landscape, allow native scrolling (don't toggle tabs)
  if (isDeviceLandscape) return false;

  const railFocused = isRailElement(target);

  if (railFocused) {
    // Check if there's a prev/next rail to navigate to
    const allRails = document.querySelectorAll(RAIL_SELECTOR);
    const currentIndex = Array.from(allRails).indexOf(target);

    const hasPrevRail = e.key === "ArrowUp" && currentIndex > 0;
    const hasNextRail = e.key === "ArrowDown" && currentIndex < allRails.length - 1;

    // If there's no rail to navigate to, toggle tabs instead
    if (!hasPrevRail && !hasNextRail) {
      e.preventDefault();
      onToggleTabsExpanded();
      return true;
    }
    // Otherwise, let the rail handler navigate between rails
    return false;
  }

  // Not on a rail, so toggle tabs
  e.preventDefault();
  onToggleTabsExpanded();
  return true;
}

/**
 * Handle Shift+Left/Right: Switch between tabs
 */
function handleTabSwitch(e: KeyboardEvent, activeTab: TabType, onSwitchTab: (tab: TabType) => void): boolean {
  if (!e.shiftKey || e.metaKey || e.ctrlKey) return false;

  if (e.key === "ArrowLeft") {
    e.preventDefault();
    const currentIndex = TITLE_TABS.indexOf(activeTab);
    const newIndex = currentIndex - 1 < 0 ? TITLE_TABS.length - 1 : currentIndex - 1;
    onSwitchTab(TITLE_TABS[newIndex]);
    return true;
  }

  if (e.key === "ArrowRight") {
    e.preventDefault();
    const currentIndex = TITLE_TABS.indexOf(activeTab);
    const newIndex = (currentIndex + 1) % TITLE_TABS.length;
    onSwitchTab(TITLE_TABS[newIndex]);
    return true;
  }

  return false;
}

/**
 * Hook for Title page keyboard shortcuts
 *
 * Global shortcuts (work unless typing in input):
 * - Space: Play title (unless rail focused)
 * - W: Toggle watchlist (applies to title, unless in nav mode on Suggested tab)
 * - L: Toggle like (applies to title, unless in nav mode on Suggested tab)
 * - Up/Down: Expand/collapse tabs section (disabled in phone landscape - allows native scroll)
 * - Shift+Left/Right: Switch tabs (meta-navigation, auto-enter/exit nav mode on Suggested tab)
 * - Enter: Navigate to focused rail tile (when in nav mode on Suggested tab)
 *
 * Note: Unmodified Left/Right reserved for rail navigation when rail has focus
 */
function useTitleKeyboardShortcuts({
  onPlay,
  onToggleWatchlist,
  onToggleLike,
  onToggleTabsExpanded,
  onSwitchTab,
  activeTab,
  watchlistMutating,
  likeMutating,
  isDeviceLandscape,
}: {
  readonly onPlay: () => void;
  readonly onToggleWatchlist: () => Promise<void>;
  readonly onToggleLike: () => Promise<void>;
  readonly onToggleTabsExpanded: () => void;
  readonly onSwitchTab: (tab: TabType) => void;
  readonly activeTab: TabType;
  readonly watchlistMutating: boolean;
  readonly likeMutating: boolean;
  readonly isDeviceLandscape: boolean;
}): void {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;

      // Ignore if typing in input
      if (shouldIgnoreKeyEvent(target)) return;

      // Try each handler in sequence (early return pattern)
      if (handleSpaceKey(e, target, onPlay)) return;
      if (handleWatchlistKey(e, onToggleWatchlist, watchlistMutating)) return;
      if (handleLikeKey(e, onToggleLike, likeMutating)) return;
      if (handleArrowUpDown(e, target, onToggleTabsExpanded, isDeviceLandscape)) return;
      if (handleTabSwitch(e, activeTab, onSwitchTab)) return;
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    onPlay,
    onToggleWatchlist,
    onToggleLike,
    onToggleTabsExpanded,
    onSwitchTab,
    activeTab,
    watchlistMutating,
    likeMutating,
    isDeviceLandscape,
  ]);
}

export function TitlePage(): ReactElement {
  const { id } = useParams();
  const [title, setTitle] = useState<ApiTitle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>("details");
  const [hasTrailer, setHasTrailer] = useState(false);

  // Detect phone landscape orientation using canonical DEVICE system
  const [isDeviceLandscape, setisDeviceLandscape] = useState(() => {
    if (globalThis.window === undefined) return false;
    const { width, height } = VIEWPORT.getDimensions();
    return DEVICE.isPhoneLandscape(width, height);
  });

  // Initialize isTabsExpanded based on phone landscape (must come after isDeviceLandscape)
  const [isTabsExpanded, setIsTabsExpanded] = useState(() => {
    if (globalThis.window === undefined) return false;
    const { width, height } = VIEWPORT.getDimensions();
    return DEVICE.isPhoneLandscape(width, height); // Start expanded in phone landscape
  });

  const { isTransitioning, transitionTo, supportsViewTransitions } = useTransitionNavigation();
  const authenticatedClient = useAuthenticatedClient();
  // Detect mobile-like devices (phone portrait, phone landscape, or tablet portrait)
  const { width, height } = VIEWPORT.getDimensions();
  const isMobile =
    DEVICE.isPhonePortrait(width, height) ||
    DEVICE.isPhoneLandscape(width, height) ||
    DEVICE.isTabletPortrait(width, height);
  const { enterNavigationMode, exitNavigationMode, isNavigationMode } = useKeyboardNavigation();

  // Track if user explicitly exited nav mode (via click) to prevent auto-re-enter
  const [userExitedNavMode, setUserExitedNavMode] = useState(false);

  // Update phone landscape detection on resize
  useEffect(() => {
    const handleResize = (): void => {
      const { width, height } = VIEWPORT.getDimensions();
      const newisDeviceLandscape = DEVICE.isPhoneLandscape(width, height);
      setisDeviceLandscape(newisDeviceLandscape);

      // Auto-expand tabs in phone landscape
      if (newisDeviceLandscape) {
        setIsTabsExpanded(true);
      }
    };

    globalThis.window.addEventListener("resize", handleResize);
    return () => globalThis.window.removeEventListener("resize", handleResize);
  }, []);

  // Ensure tabs are expanded when entering phone landscape (also cover initial mount)
  useEffect(() => {
    if (isDeviceLandscape) {
      setIsTabsExpanded(true);
    }
  }, [isDeviceLandscape]);

  // Initialize engagement hooks with the actual title ID (not the route param which could be a slug)
  const likeHook = useTitleLike(title?.id || "");
  const watchlistHook = useWatchlist(title?.id || "");

  useEffect(() => {
    async function loadTitle(): Promise<void> {
      if (!id) {
        setError("No title ID provided");
        setLoading(false);
        return;
      }

      try {
        const response = await apiCall("/v1/titles");
        if (!response.ok) {
          throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }
        const titles = (await response.json()) as ApiTitle[];

        const foundTitle = titles.find((t) => t.id === id || t.slug === id);

        if (foundTitle) {
          setTitle(foundTitle);

          // Check if title has a trailer asset
          if (authenticatedClient) {
            try {
              const assets = await authenticatedClient.listTitleAssets(foundTitle.id);
              const trailerExists = assets.some((asset: ApiAsset) => asset.kind === "TRAILER");
              setHasTrailer(trailerExists);
            } catch (error_) {
              console.warn("⚠️ Failed to check for trailer:", error_);
              // Don't fail the whole page load if assets check fails
              setHasTrailer(false);
            }
          }
        } else {
          setError("Title not found");
        }
        setLoading(false);
      } catch (err) {
        console.error("❌ Failed to load title:", err);
        setError("Failed to load title details");
        setLoading(false);
      }
    }

    void loadTitle();
  }, [id, authenticatedClient]);

  const handleBack = (): void => {
    transitionTo("/home", pageTransitions.fullScreenFade);
  };

  const handlePlay = (): void => {
    if (!title?.slug || !authenticatedClient) return;

    // Use preloading during the transition
    transitionTo(`/home/watch/${title.slug}`, pageTransitions.cinematicFade, {
      preload: () => preloadPlayerData(title.slug, authenticatedClient),
    });
  };

  const handlePlayTrailer = (): void => {
    if (!title?.slug || !authenticatedClient) return;

    // Use preloading during the transition
    transitionTo(`/home/watch/${title.slug}?assetKind=TRAILER`, pageTransitions.cinematicFade, {
      preload: () => preloadPlayerData(title.slug, authenticatedClient, { assetKind: "TRAILER" }),
    });
  };

  // Set up keyboard shortcuts for Title page actions
  useTitleKeyboardShortcuts({
    onPlay: handlePlay,
    onToggleWatchlist: watchlistHook.toggle,
    onToggleLike: likeHook.toggle,
    onToggleTabsExpanded: () => setIsTabsExpanded(!isTabsExpanded),
    onSwitchTab: setActiveTab,
    activeTab,
    watchlistMutating: watchlistHook.mutating,
    likeMutating: likeHook.mutating,
    isDeviceLandscape,
  });

  // Auto-enter navigation mode when switching to Suggested tab
  // Auto-exit navigation mode when switching away from Suggested tab
  // But respect user's explicit exit (via click) and don't auto-re-enter
  useEffect(() => {
    if (activeTab === "suggested" && !isNavigationMode && !userExitedNavMode) {
      enterNavigationMode();
    } else if (activeTab !== "suggested" && isNavigationMode) {
      exitNavigationMode();
      // Reset the flag when leaving Suggested tab
      setUserExitedNavMode(false);
    }
  }, [activeTab, isNavigationMode, userExitedNavMode, enterNavigationMode, exitNavigationMode]);

  // Detect when user exits nav mode (to prevent auto-re-enter)
  useEffect(() => {
    // If we're on Suggested tab and nav mode just turned off, user must have exited
    if (activeTab === "suggested" && !isNavigationMode) {
      setUserExitedNavMode(true);
    }
  }, [activeTab, isNavigationMode]);

  // Show error only after loading completes (prevents flash during transition)
  if (error || (!title && !loading)) {
    return (
      <div className="bg-transparent min-h-screen flex items-center justify-center">
        <div className="text-red-400 text-xl">{error || "Title not found"}</div>
      </div>
    );
  }

  // Still loading - render minimal structure to prevent transition flash
  if (!title) {
    return (
      <motion.div
        className="bg-transparent min-h-screen flex flex-col overflow-hidden content-rails"
        {...(supportsViewTransitions
          ? {}
          : {
              initial: "initial",
              animate: "animate",
              variants: pageTransitions.cinematicFade,
            })}
      >
        {/* Empty structure during load - View Transitions will crossfade smoothly */}
      </motion.div>
    );
  }

  return (
    <motion.div
      className={`bg-transparent flex flex-col content-rails ${
        isDeviceLandscape ? "min-h-screen overflow-y-auto" : "min-h-screen overflow-hidden"
      }`}
      {...(supportsViewTransitions
        ? {}
        : {
            initial: "initial",
            animate: isTransitioning ? "exit" : "animate",
            exit: "exit",
            variants: pageTransitions.cinematicFade,
          })}
    >
      {/* Back Button - Below header, fixed position, responsive to header height */}
      <div className="absolute top-16 md:top-[72px] lg:top-20 right-3 md:right-4 lg:left-8 z-50 pointer-events-auto">
        <Button onClick={handleBack} variant="ghost-sm" aria-label="Go back">
          <ArrowLeft size={18} className="md:w-5 md:h-5" />
        </Button>
      </div>

      {/* Backdrop Image - The picture behind everything */}
      <div className={isDeviceLandscape ? "fixed inset-0" : "absolute inset-0"}>
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${title.backdropUrlHd || title.heroUrl || title.posterUrlHd || title.posterUrl})`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-black/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />
        {/* Extended gradient for phone landscape scrolling */}
        <div
          className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/35 to-transparent ${
            isDeviceLandscape ? "h-[1200px]" : "h-[600px]"
          }`}
        />
      </div>

      {/* The Basket (TitleContent) - Sits on top, slides up when expanded */}
      <div
        className={
          isDeviceLandscape ? "relative pt-[calc(100vh-190px)] md:pt-[calc(100vh-250px)]" : "relative flex-1 pt-6"
        }
      >
        <TitleContent
          title={title}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isTabsExpanded={isTabsExpanded}
          onToggleTabsExpanded={() => setIsTabsExpanded(!isTabsExpanded)}
          isMobile={isMobile}
          isDeviceLandscape={isDeviceLandscape}
          onPlay={handlePlay}
          onPlayTrailer={handlePlayTrailer}
          hasTrailer={hasTrailer}
          likeState={{
            liked: likeHook.liked,
            totalLikes: likeHook.totalLikes,
            toggle: likeHook.toggle,
            mutating: likeHook.mutating,
          }}
          watchlistState={{
            inWatchlist: watchlistHook.inWatchlist,
            toggle: watchlistHook.toggle,
            mutating: watchlistHook.mutating,
          }}
          transitionTo={transitionTo}
        />
      </div>
    </motion.div>
  );
}
